import { AlertTriangle, LoaderCircle, RefreshCw, WifiOff } from 'lucide-solid';
import { ErrorBoundary, Match, Show, Switch, type JSX } from 'solid-js';
import { AppShell } from './components/AppShell';
import { ConsoleUpdateNotice } from './components/ConsoleUpdateNotice';
import { LocalConsoleSetup } from './components/LocalConsoleSetup';
import { SignIn } from './components/SignIn';
import { InlineNotice } from './components/ui';
import { reportError } from './lib/plainErrors';
import { ConnectScreen } from './screens/ConnectScreen';
import { EffectsScreen } from './screens/EffectsScreen';
import { MusicScreen } from './screens/MusicScreen';
import { PanelScreen } from './screens/PanelScreen';
import { ScoresScreen } from './screens/ScoresScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { StatusScreen } from './screens/StatusScreen';
import { useVenue, VenueProvider } from './state/VenueProvider';

function ConfigurationNeeded(): JSX.Element {
  return (
    <main class="system-state">
      <img src="/brand/nuance-logo-256.png" alt="" />
      <span><AlertTriangle size={22} /></span>
      <h1>This console is not set up yet</h1>
      <p>It could not reach the Nuance Venue cloud. Ask whoever set this console up to finish configuring it, or open the controller’s own console on the venue network.</p>
    </main>
  );
}

function FatalState(props: { error: unknown; reset: () => void }): JSX.Element {
  return (
    <main class="system-state">
      <span><WifiOff size={22} /></span>
      <h1>The console hit a snag</h1>
      <p>{reportError('console', props.error, 'Something in the console stopped working. Reloading usually clears it.').message}</p>
      <button class="button button-primary" onClick={props.reset}><RefreshCw size={17} /> Try again</button>
    </main>
  );
}

/**
 * Everything the console is, behind its gates: configured, booted, signed in, paired.
 *
 * Exported for the same reason `VenueContext` is — a journey test can mount the REAL gates against a
 * stated room, which is the only way to prove what someone at a given moment actually sees. `App` below
 * is this plus the provider that fills the context in, and a provider is exactly what a test must
 * replace. Application code renders `App`.
 */
export function Console(): JSX.Element {
  const venue = useVenue();
  return (
    <Show when={venue.configured || venue.localOrigin} fallback={<ConfigurationNeeded />}>
      <Show when={venue.authReady()} fallback={<main class="boot-state"><img src="/brand/nuance-logo-256.png" alt="" /><LoaderCircle class="spin" size={25} /><p>Opening the console…</p></main>}>
        {/*
          * `signedIn`, not `user()`: an account whose address has not been proven is signed in to
          * Firebase but is not admitted here, because the cloud will not grant it a membership or let
          * it claim an invite until the address is verified. It waits on the sign-in card instead.
          */}
        <Show when={venue.signedIn()} fallback={<SignIn />}>
          {/*
            * The controller-served bundle is the cable console: one screen asking for the venue's
            * setup code, then the whole console over that direct connection. It behaves the same way
            * whether or not the venue's internet happens to be reachable.
            */}
          <Show when={!venue.localOrigin || venue.lanHello()?.authenticated} fallback={<LocalConsoleSetup />}>
            <AppShell>
              <Show when={!venue.localOrigin}><ConsoleUpdateNotice /></Show>
              <Show when={venue.localOrigin && (venue.lanState() === 'reconnecting' || venue.bluetoothState() === 'reconnecting')}>
                <InlineNotice tone="warning" title="Reconnecting…">
                  The controller connection dropped. Controls are paused; the latest room state will resync before control returns.
                </InlineNotice>
              </Show>
              <Show when={!venue.cloudReachable()}>
                <InlineNotice tone="warning" title="Internet connection lost">
                  {/*
                    * Only said when it is true and useful. A hosted console with no direct connection
                    * has nothing to offer here, and there is no longer a place to set one up from: the
                    * direct connection is the controller's own console, reached over a cable.
                    */}
                  <Show when={(venue.lanState() === 'connected' && venue.lanHello()?.authenticated) || venue.bluetoothState() === 'connected'}>
                    {`Room control is still available${venue.bluetoothState() === 'connected' ? ' over Bluetooth' : ' over the venue network'}. Venue details will return automatically.`}
                  </Show>
                </InlineNotice>
              </Show>
              <Show when={venue.cloudError()}>
                <InlineNotice tone="danger" title="Cloud connection issue" onDismiss={venue.clearCloudError}>
                  {venue.cloudError()}
                </InlineNotice>
              </Show>
              <Switch>
                <Match when={venue.route() === 'connect'}><ConnectScreen /></Match>
                <Match when={venue.route() === 'scores'}><ScoresScreen /></Match>
                <Match when={venue.route() === 'scenes'}><PanelScreen /></Match>
                <Match when={venue.route() === 'effects'}><EffectsScreen /></Match>
                <Match when={venue.route() === 'music'}><MusicScreen /></Match>
                <Match when={venue.route() === 'status'}><StatusScreen /></Match>
                <Match when={venue.route() === 'settings'}><SettingsScreen /></Match>
              </Switch>
            </AppShell>
          </Show>
        </Show>
      </Show>
    </Show>
  );
}

export default function App(): JSX.Element {
  return (
    <ErrorBoundary fallback={(error, reset) => <FatalState error={error} reset={reset} />}>
      <VenueProvider><Console /></VenueProvider>
    </ErrorBoundary>
  );
}
